import type { UtmSearch } from '@/schemas/utm';

import { pickUtmValues } from '@/schemas/utm';

/** The element id each signup form is known by, as the tag container spells it. */
const HIDDEN_FIELD_ID = {
  participant: 'utms-participant',
  researcher: 'utms-researcher',
} as const;

export type SignupSide = keyof typeof HIDDEN_FIELD_ID;

/**
 * An empty hidden field the Google Tag Manager container fills with campaign
 * attribution.
 *
 * The container keeps the first `utm_*` values a browser arrives with for 30
 * days, and writes them back — as a query string, `utm_source=…&utm_medium=…`
 * — into whichever of these two ids it finds on the page. So a visitor who
 * follows an ad, reads for a week and signs up from a bare URL is still
 * attributed to the campaign that brought them.
 *
 * Reading the URL of the page the form is on, which is what this app does on
 * its own, is empty in exactly that case. The container's store covers it, and
 * the id is the contract it was built against: the tag looks for the element,
 * so a form that renders none contributes nothing and the tag stays silent.
 * These ids are also what the legacy signup forms used, so the same tag serves
 * both while they run side by side.
 *
 * Rendered with no `value` and no `defaultValue`, which is what lets a write
 * from outside React survive: with either prop React owns the attribute and
 * rewrites it on every commit, emptying the field between the tag filling it
 * and the visitor submitting. `utm-hidden-field.test.tsx` holds that.
 */
export function UtmHiddenField({ side }: { side: SignupSide }) {
  const id = HIDDEN_FIELD_ID[side];
  return <input type="hidden" id={id} name={id} />;
}

/**
 * The `utm_*` values the container has written into the field, or none.
 *
 * Read when the form is submitted rather than when it renders: the container
 * fills the field from its own trigger, after mount and again on each
 * client-side navigation, so a value read at render is the value from before
 * the tag ran.
 *
 * Whatever is in the field is a string this app did not write, so it is parsed
 * the same way a URL's search is: unknown keys contribute nothing, and values
 * are trimmed and bounded.
 */
export function readUtmHiddenField(side: SignupSide): UtmSearch {
  const element = globalThis.document?.getElementById(HIDDEN_FIELD_ID[side]);
  if (!element || !(element instanceof HTMLInputElement) || !element.value) {
    return {};
  }
  return pickUtmValues(Object.fromEntries(new URLSearchParams(element.value)));
}
